import React, { useState } from 'react';
import { connectionsAPI } from '../../services/api';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const intervalLabel = (minutes) => {
  if (minutes === 1440) return 'Daily';
  if (minutes >= 60) return `Every ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `Every ${minutes} minutes`;
};

/** How often to sync, which orders count, the low-stock level and how much order history to read. */
export default function ShopifySettings({ connector, connection, companyId, onSaved }) {
  const s = connection.definitions || {};
  const [draft, setDraft] = useState({
    interval: connection.syncIntervalMinutes,
    includeTestOrders: Boolean(s.includeTestOrders),
    lowStockThreshold: s.lowStockThreshold ?? 5,
    historyMonths: s.historyMonths ?? '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = (patch) => {
    setSaved(false);
    setDraft((d) => ({ ...d, ...patch }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const res = await connectionsAPI.saveSettings(connector.id, companyId, {
        syncIntervalMinutes: draft.interval,
        settings: {
          includeTestOrders: draft.includeTestOrders,
          lowStockThreshold: Number(draft.lowStockThreshold),
          historyMonths: draft.historyMonths === '' ? null : Number(draft.historyMonths),
        },
      });
      setSaved(true);
      onSaved(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="connection-settings" onSubmit={save}>
      <label className="connection-field">
        <span>Sync frequency</span>
        <select value={draft.interval} onChange={(e) => set({ interval: Number(e.target.value) })}>
          {connector.syncIntervals.map((m) => <option key={m} value={m}>{intervalLabel(m)}</option>)}
        </select>
      </label>

      <label className="connection-field">
        <span>Low-stock level</span>
        <input type="number" min="0" step="1" value={draft.lowStockThreshold} onChange={(e) => set({ lowStockThreshold: e.target.value })} />
      </label>
      <p className="source-hint">An item tracked by Shopify counts as low stock when this many or fewer are available at a location.</p>

      <label className="connection-field">
        <span>Months of order history</span>
        <input type="number" min="1" max="240" step="1" placeholder="All" value={draft.historyMonths} onChange={(e) => set({ historyMonths: e.target.value })} />
      </label>
      <p className="source-hint">Leave empty to read every order. Customers whose earlier orders are outside this window count as new.</p>

      <fieldset className="connection-field">
        <legend>Test orders</legend>
        <p className="source-hint">Orders placed with Shopify&apos;s test payments are left out unless this is ticked, such as for a store still being set up.</p>
        <div className="connection-checks">
          <label>
            <input type="checkbox" checked={draft.includeTestOrders} onChange={(e) => set({ includeTestOrders: e.target.checked })} />
            Include test orders
          </label>
        </div>
      </fieldset>

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="connection-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        {saved && <span className="source-hint connection-saved">Saved. The next sync uses these settings.</span>}
      </div>
    </form>
  );
}
